import React from 'react';

import { Checkbox, FormControlLabel, SxProps, Typography } from '@mui/material';
import { translateBlockDescription } from 'i18n';
import TextInfo from 'ui/TextInfo/TextInfo';
import {
    TreeValue,
    useTreeValue,
} from 'routes/Explore/components/Sentence/contexts/TreeValueContext';
import { useIsMultiselect } from 'routes/Explore/components/Sentence/contexts/IsMultiselectContext';
import { SentenceTreeItem } from 'routes/Explore/components/Sentence/components/TreeDropdown';
import { TreeItem } from '@mui/x-tree-view';

type Props = {
    domain: string;
    field: SentenceTreeItem;
    onClick: (field: TreeValue) => void;
    sx?: SxProps;
    testid: string;
};

const TreeItemField = (props: Props): React.ReactElement => {
    const { field, domain, onClick, testid, sx } = props;
    const descriptionKey = field.field.includes('.')
        ? `${domain}.${field.field}`
        : `${domain}.${field.field}.`;
    const description = translateBlockDescription(descriptionKey);
    const hasDescription = description && description !== descriptionKey;
    const capitalizedLabel =
        // because of display: flex, the first letter is not capitalized since ::first-letter only work with "block"
        field.label.charAt(0).toUpperCase() + field.label.slice(1);
    const { currentValues } = useTreeValue();
    const isMultiselect = useIsMultiselect();
    const handleClick = React.useCallback(
        (e: React.MouseEvent) => {
            // otherwise the action will be triggered multiple times
            e.preventDefault();
            return onClick(field.field);
        },
        [field, onClick],
    );
    const staticLabel = (
        <Typography
            variant="body1"
            // otherwise the width does not match the content 🤷‍♂️
            // there is a bug somewhere, because when setting flex:1 here it does not work
            // but in the inspector it gets fine if you switch to flex:2 than back to flex:1
            sx={{ display: 'flex' }}
        >
            {capitalizedLabel}
        </Typography>
    );
    const checkboxWithLabel = (
        <FormControlLabel
            control={
                <Checkbox
                    checked={currentValues.includes(field.field)}
                    name={field.field}
                    sx={{
                        '& .MuiSvgIcon-root': { fontSize: 16 },
                        p: '0 4px 0 0',
                    }}
                />
            }
            label={staticLabel}
            sx={{ mr: 1 }}
        />
    );
    const labelComponent = isMultiselect ? checkboxWithLabel : staticLabel;
    return field.label ? (
        <TreeItem
            key={field.field}
            onClick={handleClick}
            nodeId={`${domain}-field-${field.field}`}
            label={
                hasDescription ? (
                    <TextInfo tooltip={description}>{labelComponent}</TextInfo>
                ) : (
                    labelComponent
                )
            }
            data-testid={`${testid}-${field.field.replace(/\./g, '_')}`}
            sx={sx}
        />
    ) : (
        <></>
    );
};

const isEqual = (prevProps: Props, nextProps: Props) =>
    prevProps.field.field === nextProps.field.field &&
    prevProps.field.label === nextProps.field.label &&
    // without this, only one field chan be checked at once
    prevProps.onClick === nextProps.onClick;

export default React.memo(TreeItemField, isEqual);
